import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

export default function Auth() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [reg, setReg] = useState({ name: "", email: "", password: "" });

  const finish = (data) => {
    setUser(data.user);
    if (data.user && !data.user.email_verified) {
      toast.success("Compte créé — vérifiez votre email.");
      navigate("/verify-email");
    } else {
      toast.success("Bienvenue !");
      navigate("/studio");
    }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", login);
      finish(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", reg);
      finish(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="studio-bg grain min-h-screen grid place-items-center px-5 py-10 relative">
      <div className="relative z-10 w-full max-w-md fade-up">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3ECF8E] to-emerald-600 grid place-items-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="font-display text-xl font-extrabold">Vira<span className="text-[#3ECF8E]">lyne</span></span>
        </Link>

        <div className="glass rounded-3xl p-7 sm:p-8">
          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 w-full bg-white/5 rounded-full p-1 mb-6">
              <TabsTrigger value="login" className="rounded-full data-[state=active]:bg-green-600 data-[state=active]:text-white" data-testid="tab-login">Connexion</TabsTrigger>
              <TabsTrigger value="register" className="rounded-full data-[state=active]:bg-green-600 data-[state=active]:text-white" data-testid="tab-register">Inscription</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input type="email" required value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })}
                    className="mt-1.5 bg-white/5 border-white/10 h-11" placeholder="vous@exemple.com" data-testid="login-email-input" />
                </div>
                <div>
                  <Label className="text-slate-300">Mot de passe</Label>
                  <Input type="password" required value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })}
                    className="mt-1.5 bg-white/5 border-white/10 h-11" placeholder="••••••••" data-testid="login-password-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full font-semibold" data-testid="login-submit-button">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Se connecter"}
                </Button>
                <button type="button" onClick={() => navigate("/forgot-password")}
                  className="text-sm text-slate-400 hover:text-[#3ECF8E] transition-colors w-full text-center" data-testid="forgot-password-link">
                  Mot de passe oublié ?
                </button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-4">
                <div>
                  <Label className="text-slate-300">Nom</Label>
                  <Input required value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })}
                    className="mt-1.5 bg-white/5 border-white/10 h-11" placeholder="Votre nom" data-testid="register-name-input" />
                </div>
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input type="email" required value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })}
                    className="mt-1.5 bg-white/5 border-white/10 h-11" placeholder="vous@exemple.com" data-testid="register-email-input" />
                </div>
                <div>
                  <Label className="text-slate-300">Mot de passe</Label>
                  <Input type="password" required minLength={6} value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })}
                    className="mt-1.5 bg-white/5 border-white/10 h-11" placeholder="Min. 6 caractères" data-testid="register-password-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full font-semibold" data-testid="register-submit-button">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Créer mon compte"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
